import type { CustomImageData } from '@iheartjane/dm-sdk';
import debounce from 'lodash-es/debounce';
import type { MouseEvent } from 'react';
import { useEffect, useEffectEvent, useRef } from 'react';

import type {
  CustomImageAdProduct,
  WebCustomImageAdEvent,
} from './webCustomImageAd.types';

const SCROLL_SETTLE_MS = 50;

const clickTarget = (
  brandBlock: HTMLElement | null,
  { clientX, clientY, detail }: MouseEvent
): 'image' | 'logo_tagline' => {
  if (!brandBlock || detail === 0) return 'image';

  const { bottom, left, right, top } = brandBlock.getBoundingClientRect();

  return clientX >= left &&
    clientX <= right &&
    clientY >= top &&
    clientY <= bottom
    ? 'logo_tagline'
    : 'image';
};

const furthestVisiblePosition = (row: HTMLElement, scrollLeft: number) => {
  const rowLeft = row.getBoundingClientRect().left - row.scrollLeft;
  let furthest = 0;

  Array.from(row.children).forEach((card, index) => {
    const { left, width } = card.getBoundingClientRect();
    const center = left - rowLeft + width / 2;

    if (center <= scrollLeft + row.clientWidth) {
      furthest = index + 1;
    }
  });

  return furthest;
};

/**
 * Gathers the `WebCustomImageAdEvent`s the ad itself can observe: creative
 * clicks, product clicks, and product-row swipes. Attach the returned refs to
 * the brand block and the products row.
 */
export const useCustomImageAdAnalytics = (
  customImageData: CustomImageData | undefined,
  onEvent: ((event: WebCustomImageAdEvent) => void) | undefined
) => {
  const brandBlockRef = useRef<HTMLDivElement>(null);
  const productsRowRef = useRef<HTMLDivElement>(null);
  const reportedSwipeRef = useRef(0);

  const onSwiped = useEffectEvent((row: HTMLElement) => {
    const furthestPosition = furthestVisiblePosition(row, row.scrollLeft);
    const initialPosition = furthestVisiblePosition(row, 0);

    if (
      !onEvent ||
      furthestPosition <= Math.max(reportedSwipeRef.current, initialPosition)
    ) {
      return;
    }

    reportedSwipeRef.current = furthestPosition;
    onEvent({
      furthestPosition,
      productCount: row.children.length,
      type: 'swiped_products',
    });
  });

  useEffect(() => {
    const row = productsRowRef.current;
    if (!row) return;

    const handleScrollSettled = debounce(() => onSwiped(row), SCROLL_SETTLE_MS);

    row.addEventListener('scroll', handleScrollSettled, { passive: true });

    return () => {
      row.removeEventListener('scroll', handleScrollSettled);
      handleScrollSettled.cancel();
    };
  }, [customImageData]);

  return {
    brandBlockRef,
    productsRowRef,
    trackCreativeClick: (event: MouseEvent) =>
      onEvent?.({
        target: clickTarget(brandBlockRef.current, event),
        type: 'clicked_ad',
      }),
    trackProductClick: (product: CustomImageAdProduct, index: number) =>
      onEvent?.({ position: index + 1, product, type: 'clicked_product' }),
  };
};
